import { useCallback, useState } from 'react';

import { definitions, operations } from '@luxuryescapes/contract-svc-promo';

import { getPromoTagSummary } from '~/services/PromoService';

// based on operations['getPromoTagSummaries']['responses']['200']['content']['application/json']
export type PromoTagSummaries = {
  dept_tag: string;
  category_tag: string;
  sub_category_tag: string;
  fixedAmount: number;
  fixedCount: number;
  percentage: number;
  percentageCount: number;
  maxFixedAmount: number;
  maxPercentageAmount: number;
  approvedByUsers: Array<string>;
  createdByUsers: Array<string>;
  updatedByUsers: Array<string>;
  unapprovedPromoCount: number;
  totalRemainingCount: number;
  percentageNoMaxDiscountCodes: string;
  percentageNoMaxDiscountCount: number;
};

export type PromoTagFilters = {
  dept_tag?: string;
  category_tag?: string;
  sub_category_tag?: string;
  brand?: definitions['Promo Brands'];
  region?: definitions['Promo Regions'];
  created_after?: string;
};

const mapPromoTagSummaries = (
  tagSummaries: operations['getPromoTagSummaries']['responses']['200']['content']['application/json']['result'],
): Array<PromoTagSummaries> => {
  return tagSummaries.map((tagSummary) => {
    return {
      ...tagSummary,
      approvedByUsers: tagSummary.approvedByUsers,
      createdByUsers: tagSummary.createdByUsers,
      updatedByUsers: tagSummary.updatedByUsers,
      totalRemainingCount: tagSummary.totalRemainingCount,
      percentageNoMaxDiscountCodes: 'Not implemented yet',
      percentageNoMaxDiscountCount: -99,
    };
  });
};

const usePromoTagSummaries = () => {
  const [tagSummaries, setTagSummaries] = useState<Array<PromoTagSummaries>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchTagSummaries = useCallback(async (filters: PromoTagFilters) => {
    setIsLoading(true);
    setError(null);

    try {
      if (!filters.category_tag) {
        delete filters.category_tag;
      }
      if (!filters.dept_tag) {
        delete filters.dept_tag;
      }
      if (!filters.sub_category_tag) {
        delete filters.sub_category_tag;
      }
      const response = await getPromoTagSummary({ filters });
      if (response.status === 200) {
        const mappedData = mapPromoTagSummaries(response.result);
        setTagSummaries(mappedData);
      } else {
        setError(new Error(response.message || 'Failed to fetch promo tag summaries'));
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('An error occurred while fetching promo tag summaries'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    tagSummaries,
    isLoading,
    error,
    refetch: (filters: PromoTagFilters) => fetchTagSummaries(filters),
  };
};

export default usePromoTagSummaries;
